import { colorManager } from "@/services/platform/color-manager";
import { platformManager } from "@/services/platform/platform-manager";
import { pictureManager } from "@/services/utilities/picture-manager";
import { loginManager } from "@/services/security/login-manager";
import { defineStore } from "pinia";
import { useMainStore } from "@/store/main/main";
import router from "@/router";
import { apiConnection } from "@/services/api-connection";

export const usePlatformStore = defineStore({
  id: "Platform",
  state: () => {
    return {
      platforms: {},
      platform: {},
      design: {},
      modulesAvailable: [],
      dataAgreement: {},
      documents: {},
      questionnaireConfig: {},
      logoCfa: null,
      logoCfaPromotion: null,
      logoHomepage: null,
      apprenticeProfileLabel: {},
      tutorProfileLabel: {},
      apprenticeMasterProfileLabel: {},
      companyManagerProfileLabel: {},
      studeaManagerProfileLabel: {},
      needUpdate: true,
      guides: [],
      dataVersionIsActive: false,
    };
  },

  actions: {
    fetchPlatformFromUser(username) {
      return platformManager.loadWebsites(username).then((platforms) => {
        this.platforms = platforms;
      });
    },

    fetchDataAgreement() {
      if (useMainStore().isInternet) {
        return platformManager.loadDataAgreement().then((dataAgreement) => {
          this.dataAgreement = dataAgreement;
        });
      }
    },

    fetchDocuments() {
      if (useMainStore().isInternet) {
        return platformManager.loadDocuments().then((documents) => {
          this.documents = documents;
        });
      }
    },

    fetchDesign(payload) {
      this.fetchDataAgreement();
      if (router.currentRoute.value.path.includes("login")) {
        this.fetchDocuments();
      }
      return platformManager.loadDesign().then((response) => {
        if (response && response.design) {
          this.setDesign(response.design);
          this.setModulesAvailable(response.modules);
          this.setDataVersionIsActive(response.dataVersionIsActive);
          colorManager.setPlatformStyle(null, response.design);
          pictureManager.preloadImages(response.design).then(() => {
            if (payload.disableLoading) {
              useMainStore().setAppLoading(false);
            }
          });
        }
        if (response && response.guides && response.guides.length) {
          this.setGuides(response.guides);
        }
      });
    },

    fetchPlatform() {
      platformManager.loadPlatform().then((platform) => {
        this.setPlatform(platform);
      });
    },

    setDataVersionIsActive(dataVersionIsActive) {
      this.dataVersionIsActive = dataVersionIsActive;
    },

    fetchConfig() {
      if (useMainStore().isInternet) {
        return platformManager.loadQuestionnaireConfig().then((data) => {
          if (data) {
            this.questionnaireConfig = data.questionnaireConfig;
            this.apprenticeProfileLabel = data.userProfileTitleApprentice;
            this.tutorProfileLabel = data.userProfileTitleTutor;
            this.apprenticeMasterProfileLabel = data.userProfileTitleApprenticeMaster;
            this.studeaManagerProfileLabel = data.userProfileTitleStudeaManager;
            this.companyManagerProfileLabel = data.userProfileTitleCompanyManager;
          }
        });
      }
    },

    loadUserProfiles() {
      return apiConnection.get("/app/config/user-profile", {}, false).then((data) => {
        if (data) {
          this.apprenticeProfileLabel = data.userProfileTitleApprentice;
          this.tutorProfileLabel = data.userProfileTitleTutor;
          this.apprenticeMasterProfileLabel = data.userProfileTitleApprenticeMaster;
          this.studeaManagerProfileLabel = data.userProfileTitleStudeaManager;
          this.companyManagerProfileLabel = data.userProfileTitleCompanyManager;
        }
      });
    },

    setDesign(design) {
      this.design = design;
      this.logoCfa = design && design.logoCfa ? pictureManager.setPicture(design.logoCfa, "login") : "";
      this.logoHomepage = design && design.logoHomepage ? pictureManager.setPicture(design.logoHomepage, "home") : "";
    },

    setModulesAvailable(modules) {
      this.modulesAvailable = modules;
    },

    setNeedUpdate(needUpdate) {
      this.needUpdate = needUpdate;
    },

    setPlatform(platform) {
      this.platform = platform;
      loginManager.setApiPath(platform);
    },

    setPlatforms(platforms) {
      this.platforms = platforms;
    },

    setLogoCfaPromotion(logo) {
      this.logoCfaPromotion = logo;
    },

    setGuides(guides) {
      this.guides = guides;
    },

    init() {
      this.platforms = {};
      this.platform = {};
      this.design = {};
      this.dataAgreement = {};
      this.documents = {};
      this.questionnaireConfig = {};
      this.logoCfa = null;
      this.logoCfaPromotion = null;
      this.logoHomepage = null;
      this.apprenticeProfileLabel = {};
      this.tutorProfileLabel = {};
      this.apprenticeMasterProfileLabel = {};
      this.companyManagerProfileLabel = {};
      this.studeaManagerProfileLabel = {};
      this.needUpdate = true;
      this.guides = [];
    },
  },
});
